import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Paper,
  Chip,
  Card,
  CardContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  Checkbox,
  FormControlLabel,
  Popover
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  CalendarToday,
  Clear,
  Refresh,
  Email,
  Phone,
  Person,
  ViewColumn
} from '@mui/icons-material';
import { investorApi } from '../../api/api';
import dayjs from 'dayjs';

const Enquiry = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [pageSize, setPageSize] = useState(25);
  const [propertyTypeFilter, setPropertyTypeFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);

  // Property type configuration
  const propertyTypeConfig = {
    Property: { color: "primary", label: "Property" },
    Project: { color: "success", label: "Project" },
    Apartment: { color: "info", label: "Apartment" },
    "Individual Villa": { color: "secondary", label: "Individual Villa" }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const res = await investorApi.getAllEnquiries();
      const data = res.data || [];
      setEnquiries(data);
      applyFilters(data, propertyTypeFilter, dateFilter, searchKeyword);
    } catch (err) {
      console.error('Error fetching enquiries:', err);
      showSnackbar("Failed to load enquiries", "error");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (data, typeFilter, dateFilter, searchFilter) => {
    let filtered = data;

    // Filter by property type
    if (typeFilter !== "All") {
      filtered = filtered.filter(
        (enq) => enq.PropertyType === typeFilter
      );
    }

    // Filter by date
    if (dateFilter) {
      filtered = filtered.filter(
        (enq) => enq.created_on && enq.created_on.startsWith(dateFilter)
      );
    }

    // Filter by search keyword
    if (searchFilter.trim() !== '') {
      const keyword = searchFilter.toLowerCase();
      filtered = filtered.filter(
        (enq) =>
          enq.Username?.toLowerCase().includes(keyword) ||
          enq.PropertyName?.toLowerCase().includes(keyword) ||
          enq.ApartmentName?.toLowerCase().includes(keyword) ||
          enq.project_name?.toLowerCase().includes(keyword) ||
          enq.Mobilenumber?.toString().includes(keyword) ||
          enq.Email?.toLowerCase().includes(keyword) ||
          enq.Message?.toLowerCase().includes(keyword)
      );
    }

    setFilteredEnquiries(filtered);
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  useEffect(() => {
    applyFilters(enquiries, propertyTypeFilter, dateFilter, searchKeyword);
  }, [propertyTypeFilter, dateFilter, searchKeyword, enquiries]);

  const formatDate = (dateString) => {
    if (!dateString) return '------';
    return dayjs(dateString).format('DD MMM YYYY, hh:mm A');
  };

  const clearFilters = () => {
    setPropertyTypeFilter("All");
    setDateFilter('');
    setSearchKeyword('');
  };

  const hasActiveFilters = propertyTypeFilter !== "All" || dateFilter || searchKeyword;

  const columns = [
    {
      field: 'EnquiryID',
      headerName: 'ID',
      width: 80,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'Username',
      headerName: 'USER',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person color="action" fontSize="small" />
          <Typography variant="body2" noWrap>
            {params.value || '------'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'Mobilenumber',
      headerName: 'MOBILE',
      width: 150,
      renderCell: params => (
        <Typography sx={{ color: '#0871c5' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'Email',
      headerName: 'EMAIL',
      width: 220,
      renderCell: params => (
        <Typography sx={{ color: '#0871c5' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'ApartmentName',
      headerName: 'APARTMENT',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || '------'}
        </Typography>
      )
    },
    {
      field: 'PropertyName',
      headerName: 'PROPERTY',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || '------'}
        </Typography>
      )
    },
    {
      field: 'project_name',
      headerName: 'PROJECT',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || '------'}
        </Typography>
      )
    },
    {
      field: 'PropertyType',
      headerName: 'TYPE',
      width: 140,
      renderCell: (params) => {
        const typeInfo = propertyTypeConfig[params.value];
        return typeInfo ? (
          <Chip
            label={typeInfo.label}
            color={typeInfo.color}
            variant="outlined"
            size="small"
          />
        ) : (
          <Typography variant="body2">
            {params.value || '------'}
          </Typography>
        );
      }
    },
    {
      field: 'Message',
      headerName: 'MESSAGE',
      width: 250,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            color: 'text.primary',
            fontStyle: params.value ? 'normal' : 'italic'
          }}
        >
          {params.value || 'No message'}
        </Typography>
      ),
    },
    {
      field: 'created_on',
      headerName: 'DATE & TIME',
      width: 180,
      renderCell: (params) => (
        <Chip
          label={formatDate(params.value)}
          color="primary"
          variant="outlined"
          size="small"
          sx={{ fontWeight: 500 }}
        />
      ),
    },
  ];

  const [selectedFields, setSelectedFields] = useState(
    columns.map((col) => col.field)
  );
  const displayedColumns = columns.filter((col) =>
    selectedFields.includes(col.field)
  );

  const handleIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "column-popover" : undefined;

  return (
    <Box sx={{ marginTop: 2 }}>

      <Box>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
          Property Enquiries
        </Typography>

      </Box>
      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
            <Typography variant="h4" fontWeight="bold" color="primary">
              {enquiries.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Enquiries
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {enquiries.filter(e => e.PropertyType === 'Property').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Properties
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {enquiries.filter(e => e.PropertyType === 'Project').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Project
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {enquiries.filter(e => e.PropertyType === 'Individual Villa').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Villa
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {enquiries.filter(e => e.PropertyType === 'Apartment').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Apartments
            </Typography>
          </Card>
        </Grid>
      </Grid>
      {/* Header */}
      <Card
        sx={{
          flexGrow: 1,
          border: "1px solid lightgray",
          borderRadius: "5px",
          marginBottom: 2
        }}
      >
        <CardContent>
          <Grid
            container
            spacing={2}
            alignItems="center"
            direction="row"
            sx={{
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            {/* Search Field */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Search By Key words"
                variant="outlined"
                fullWidth
                size="small"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                }}
              />
            </Grid>

            {/* Date Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Date"
                type="date"
                variant="outlined"
                size="small"
                fullWidth
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Property Type Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Property Type</InputLabel>
                <Select
                  value={propertyTypeFilter}
                  onChange={(e) => setPropertyTypeFilter(e.target.value)}
                  label="Property Type"
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Property">Property</MenuItem>
                  <MenuItem value="Project">Project</MenuItem>
                  <MenuItem value="Apartment">Apartment</MenuItem>
                  <MenuItem value="Individual Villa">Individual Villa</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Clear Filters Button */}
            <Grid item xs={12} sm={6} md={2}>
              <IconButton
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                sx={{
                  color: hasActiveFilters ? '#1976d2' : '#ccc',
                  border: '1px solid',
                  borderColor: hasActiveFilters ? '#1976d2' : '#ccc',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: hasActiveFilters ? '#e3f2fd' : 'transparent'
                  }
                }}
              >
                <Clear />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Clear Filters
                </Typography>
              </IconButton>
            </Grid>

            {/* Icon Button (Right end) */}
            <Grid item xs={12} sm="auto" md="auto">
              <IconButton onClick={handleIconClick}>
                <ViewColumn />
              </IconButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>



      {/* Data Grid */}
      <Card style={{ height: "100vh", overflowX: "hidden", marginTop: "10px", backgroundColor: "#f4f1f1ff" }}>
        <Box sx={{ height: 600, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={filteredEnquiries}
              columns={displayedColumns}
              getRowId={(row) => row.EnquiryID}
              pageSize={pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              disableSelectionOnClick
              loading={loading}
              sx={{
                height: 650,
                border: '1px solid #ccc',
                borderRadius: 2,
                fontSize: '0.9rem',
                backgroundColor: '#ffffff',
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f0f0f0ff',
                },
                '& .MuiDataGrid-cell': {
                  color: '#333',
                  borderBottom: '1px solid #ddd',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f0f0f0ff',
                  color: 'black',
                  fontWeight: 'bold',
                },
                '& .MuiTablePagination-root': {
                  fontSize: '0.875rem',
                },
              }}
            />
          )}
        </Box>
      </Card>

      {/* Column Selection Popover */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="subtitle1" gutterBottom>
            Select Columns
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {columns.map((col) => (
              <FormControl
                key={col.field}
                component="fieldset"
                sx={{ m: 0, p: 0 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedFields.includes(col.field)}
                      onChange={() => {
                        setSelectedFields((prev) =>
                          prev.includes(col.field)
                            ? prev.filter((f) => f !== col.field)
                            : [...prev, col.field]
                        );
                      }}
                      sx={{
                        '&.Mui-checked': {
                          color: 'skyblue',
                        },
                      }}
                    />
                  }
                  label={col.headerName}
                  sx={{ m: 0 }}
                />
              </FormControl>
            ))}
          </Box>
        </Box>
      </Popover>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Enquiry;